import {
  ArrowRight,
  CloudSun,
  Clock,
  Cpu,
  Drama,
  Dumbbell,
  GraduationCap,
  HeartPulse,
  Home,
  MapPin,
  Palette,
  PawPrint,
  Plane,
  Shirt,
  ShoppingCart,
  Smile,
  Trees,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Card } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { practicePaletteFor } from '@/entities/practice';
import { haptics } from '@/shared/lib/telegram/haptics';
import { TopicRouletteSheet } from './TopicRouletteSheet';

interface TeaserTopic {
  readonly en: string;
  readonly Icon: LucideIcon;
}

/**
 * Названия для живой ленты — не список рулетки (тот в `entities/topic`, 50
 * тем под диалог), а витрина: захардкожены по реальным темам из таблицы
 * `topics` (source `core`, та же, что видит «Словарь»). Нарочно только на
 * английском, даже при русском интерфейсе — витрина демонстрирует темы
 * практики английского, а не переводит UI.
 */
const TEASER_ROWS: readonly (readonly TeaserTopic[])[] = [
  [
    { en: 'Emotions', Icon: Smile },
    { en: 'Travel', Icon: Plane },
    { en: 'Food', Icon: UtensilsCrossed },
    { en: 'Technology', Icon: Cpu },
    { en: 'Sports/Hobbies', Icon: Dumbbell },
    { en: 'Family', Icon: Users },
  ],
  [
    { en: 'Nature', Icon: Trees },
    { en: 'City/Places', Icon: MapPin },
    { en: 'Health', Icon: HeartPulse },
    { en: 'Money/Shopping', Icon: ShoppingCart },
    { en: 'Culture/Art', Icon: Drama },
    { en: 'Weather', Icon: CloudSun },
  ],
  [
    { en: 'Animals', Icon: PawPrint },
    { en: 'Home', Icon: Home },
    { en: 'Clothing', Icon: Shirt },
    { en: 'Education', Icon: GraduationCap },
    { en: 'Time', Icon: Clock },
    { en: 'Colors', Icon: Palette },
  ],
];

// Медленно и по-разному, чтобы три строки не ехали синхронно одним блоком.
const ROW_DURATIONS_S = [55, 65, 75];

/**
 * Одна строка ленты: список тем продублирован — анимация едет от 0 до -50%
 * своей ширины, и второй набор бесшовно подхватывает эстафету там, где
 * закончился первый. Отступ между чипами — `marginRight` на каждом, а не
 * `gap` на контейнере: `gap` добавляет ровно один зазор на стыке половин
 * ПОВЕРХ уже посчитанных 50%, отчего в момент склейки половин был лишний
 * полу-зазор и чипы у шва «моргали» — margin считается внутри ширины самого
 * чипа, поэтому 50% ширины продублированного ряда попадают точно на границу
 * между первым и вторым набором. `direction` инвертирует анимацию
 * (`reverse`) — у соседних строк она разная, отчего лента не едет монолитно
 * в одну сторону.
 */
function TeaserRow({
  topics,
  paletteStart,
  direction,
  duration,
}: {
  topics: readonly TeaserTopic[];
  paletteStart: number;
  direction: 'left' | 'right';
  duration: number;
}) {
  const doubled = [...topics, ...topics];
  return (
    <div className="overflow-hidden">
      <div
        className={`flex w-max ${direction === 'left' ? 'anim-marquee-left' : 'anim-marquee-right'}`}
        style={{ animationDuration: `${duration}s`, willChange: 'transform' }}
      >
        {doubled.map(({ en, Icon }, index) => {
          // По кругу палитры «Готовых тем» (`entities/practice`), а не своя
          // тройка тонов — так чипы читаются продолжением того же
          // цветового языка, что и карточки ролевых игр/идиом ниже.
          const { from, to } = practicePaletteFor(paletteStart + (index % topics.length));
          return (
            <span
              key={index}
              className="mr-3 flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-medium text-ink"
              style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
            >
              <Icon className="size-3.5 text-ink-muted" aria-hidden="true" />
              {en}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Промо-карточка рулетки тем на главном экране: открывает шторку с барабанами
 * (`TopicRouletteSheet`). Раньше вела на отдельную страницу `widgets/topic-picker-entry`
 * — та пока жива как запасной вариант, но отсюда на неё не ведёт.
 */
export function TopicPickerEntryCard() {
  const { t } = useTranslation('TopicPickerEntryCard');
  const [isSheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="block w-full text-left transition-transform active:scale-[0.97]"
        onClick={() => {
          haptics.advance();
          setSheetOpen(true);
        }}
      >
        <Card className="flex flex-col gap-4 overflow-hidden p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <p className="text-[15px] font-semibold leading-snug text-ink">{t('title')}</p>
              <p className="text-xs leading-snug text-ink-muted">{t('subtitle')}</p>
            </div>
            {/* Декоративная стрелка-аффорданс, не своя кнопка — клик уже висит
              на всей карточке (`button` снаружи), вложенный интерактивный
              элемент внутри него был бы невалидным HTML. */}
            <span
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white shadow-lg"
              aria-hidden="true"
            >
              <ArrowRight className="size-4 text-ink" />
            </span>
          </div>

          <div className="relative -mx-4">
            <div className="flex flex-col gap-3">
              {TEASER_ROWS.map((topics, index) => (
                <TeaserRow
                  key={index}
                  topics={topics}
                  paletteStart={index * topics.length}
                  direction={index % 2 === 0 ? 'left' : 'right'}
                  duration={ROW_DURATIONS_S[index]!}
                />
              ))}
            </div>

            {/* Затухание по краям — сплошным белым (`--color-glass`, фон
              карточки) градиентом поверх ленты, не `mask-image` на самой
              анимирующейся ленте: маска на постоянно двигающемся контенте
              заставляет браузер перерисовывать её каждый кадр (не может
              отдать слой в чистый GPU-композитинг), что и давало то самое
              дёргание — статичный оверлей сверху рендерится один раз и в
              анимацию рядов не вмешивается. */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-12"
              aria-hidden="true"
              style={{
                background: 'linear-gradient(to right, var(--color-glass) 0%, transparent 100%)',
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-12"
              aria-hidden="true"
              style={{
                background: 'linear-gradient(to left, var(--color-glass) 0%, transparent 100%)',
              }}
            />
          </div>
        </Card>
      </button>
      {isSheetOpen && <TopicRouletteSheet onClose={() => setSheetOpen(false)} />}
    </>
  );
}
